import { z } from 'zod';
import {
	SLOT_MODE,
	type AddCounterpartyFormValues,
	type AssignableUser,
	type SlotFormValues,
	type SlotSubmission,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.types';

/** A blank slot, open until someone chooses to assign or invite. */
export const emptySlot = (): SlotFormValues => ({
	mode: SLOT_MODE.UNASSIGNED,
	userId: '',
	firstName: '',
	lastName: '',
	email: '',
});

/** Only the rendered slots are submitted, and only the branch the manager chose is sent — values
 *  left behind by switching modes must not reach the endpoint, which refuses a slot carrying both. */
export function toSlotSubmissions(values: AddCounterpartyFormValues): SlotSubmission[] {
	return values.slots.slice(0, values.slotCount).map((slot) => {
		if (slot.mode === SLOT_MODE.USER) {
			return { userId: slot.userId };
		}
		if (slot.mode === SLOT_MODE.INVITE) {
			return {
				invite: {
					firstName: slot.firstName.trim(),
					lastName: slot.lastName.trim(),
					email: slot.email.trim(),
				},
			};
		}
		return {};
	});
}

/** Cross-field slot validation shared by AddCounterpartyModal (single-add) and
 *  BulkAddCounterpartyModal (bulk) — only the slots actually rendered (up to `slotCount`) have to
 *  be complete, and only for the branch the manager picked. One person cannot fill two slots of
 *  the same party — two signatories means two people; the server refuses it either way, so
 *  flagging it here keeps the manager out of a 400 (on every document, for the bulk path). */
export function addCounterpartySlotValidation(t: (key: string) => string) {
	return (values: { slotCount: number; slots: SlotFormValues[] }, ctx: z.RefinementCtx): void => {
		const seenUserIds = new Set<string>();
		const seenEmails = new Set<string>();
		values.slots.slice(0, values.slotCount).forEach((slot, index) => {
			if (slot.mode === SLOT_MODE.USER && !slot.userId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['slots', index, 'userId'],
					message: t('documents_page.add_counterparty.modal.user_required'),
				});
			}
			if (slot.mode === SLOT_MODE.USER && slot.userId) {
				if (seenUserIds.has(slot.userId)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ['slots', index, 'userId'],
						message: t('documents_page.add_counterparty.modal.user_duplicate'),
					});
				}
				seenUserIds.add(slot.userId);
			}
			if (slot.mode !== SLOT_MODE.INVITE) {
				return;
			}
			if (!slot.firstName.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['slots', index, 'firstName'],
					message: t('documents_page.add_counterparty.modal.first_name_required'),
				});
			}
			if (!slot.lastName.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['slots', index, 'lastName'],
					message: t('documents_page.add_counterparty.modal.last_name_required'),
				});
			}
			const email = slot.email.trim();
			if (!z.string().email().safeParse(email).success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['slots', index, 'email'],
					message: t('documents_page.add_counterparty.modal.email_invalid'),
				});
				return;
			}
			const normalisedEmail = email.toLowerCase();
			if (seenEmails.has(normalisedEmail)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['slots', index, 'email'],
					message: t('documents_page.add_counterparty.modal.email_duplicate'),
				});
			}
			seenEmails.add(normalisedEmail);
		});
	};
}

/** Programme users as the slot picker wants them: an id and one display label. */
export function toAssignableUsers(
	programmeUsers: ReadonlyArray<{
		id: string;
		firstName?: string | null;
		lastName?: string | null;
		email?: string | null;
	}>
): AssignableUser[] {
	return programmeUsers.map((user) => {
		const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
		return { id: user.id, label: fullName || user.email || user.id };
	});
}
